import { isMultiSizeArray, isSingleSizeArray, isSizeObject, isSlotTargetingEntry, isTupleNumberArray, } from "@/ts/guards";
import type { AmazonModuleConfig, GPTModuleConfig, MagniteModuleConfig, } from "@/ts/module";
import type { AdUnits } from "@/ts/prebid/types";
import type { AdSizesConfig, AmazonSlotMapEntry, DisplaySlotConfig, ExternalSlotTargeting, MagniteSize, MagniteSlotMapEntry, SlotPattern, SlotTargeting, VideoSlotConfig, } from "@/ts/types";
import { getGoogleTag } from "./googletag";
const REPEAT_SLOT_PATTERNS: SlotPattern[] = [
    {
        regex: /^div-gpt-ad-mid-article-slot-repeat(-\d+)?$/,
        baseKey: "div-gpt-ad-mid-article-slot-repeat",
    },
    {
        regex: /^div-gpt-ad-big-iv-article-slot(-\d+)?$/,
        baseKey: "div-gpt-ad-big-iv-article-slot",
    },
    {
        regex: /^div-gpt-ad-iv-article-slot(-\d+)?$/,
        baseKey: "div-gpt-ad-iv-article-slot",
    },
    {
        regex: /^div-gpt-ad-iv-article-banner-slot(-\d+)?$/,
        baseKey: "div-gpt-ad-iv-article-banner-slot",
    },
    {
        regex: /^div-gpt-ad-big-iv-article-mob-slot(-\d+)?$/,
        baseKey: "div-gpt-ad-big-iv-article-mob-slot",
    },
    {
        regex: /^div-gpt-ad-live-event-slot-repeat(-\d+)?$/,
        baseKey: "div-gpt-ad-live-event-slot-repeat",
    },
];
const REFRESH_MIN_WIDTH = 50;
export const findSlotPattern = (key: string): SlotPattern | undefined => REPEAT_SLOT_PATTERNS.find(({ regex }) => regex.test(key));
export const isRepeatLikeSlot = (key: string): boolean => Boolean(findSlotPattern(key));
export const resolveRepeatSlots = (key: string, adUnits: AdUnits) => {
    const pattern = findSlotPattern(key);
    if (!pattern) {
        return adUnits[key]; // return the key's own configuration
    }
    return {
        ...adUnits[pattern.baseKey], // If the key is a "repeat-n" pattern and "repeat" exists in adUnits, use "repeat"
        code: key,
    };
};
export const withAdUnitCode = (slotId: string, adUnits: AdUnits) => ({
    ...adUnits[slotId],
    code: slotId,
});
export const getSizeForSlot = (slotId: string, config: AmazonModuleConfig["adSizesAmazon"] | MagniteModuleConfig["adSizesMagnite"] | GPTModuleConfig["adSizesGPT"] | undefined) => {
    if (!config) {
        return [];
    }
    const pattern = findSlotPattern(slotId);
    return config[pattern?.baseKey ?? slotId];
};
export const filterSlotBySize = (adSizesCfg: AdSizesConfig) => (cmdSlot: DisplaySlotConfig) => {
    return (adSizesCfg &&
        (adSizesCfg[cmdSlot.slotId] ||
            // This handles repeating slots and recommender slots
            Object.keys(adSizesCfg).some((prefix) => cmdSlot.slotId.startsWith(prefix))));
};
const isMagniteSlotMap = (slot: AmazonSlotMapEntry | MagniteSlotMapEntry): slot is MagniteSlotMapEntry => "divId" in slot;
const getSizesForViewport = (sizeMapping: googletag.SizeMappingArray, width: number) => {
    const sortedMapping = [...sizeMapping].sort(([viewportA], [viewportB]) => viewportB[0] - viewportA[0]);
    for (const [viewport, sizes] of sortedMapping) {
        if (width >= viewport[0]) {
            return sizes;
        }
    }
    return [];
};
export const removeUnneededSizes = <T extends AmazonSlotMapEntry | MagniteSlotMapEntry,>(slotMap: T): T => {
    const { sizes, sizeMapping } = slotMap;
    if (sizeMapping) {
        const width = window.innerWidth;
        const viewportSizes = getSizesForViewport(sizeMapping, width);
        // NamedSize is not supported in Amazon / Magnite
        // https://developers.google.com/publisher-tag/reference#googletag.NamedSize
        if (viewportSizes.length > 0 && isTupleNumberArray(viewportSizes)) {
            const filteredSizes = isMagniteSlotMap(slotMap)
                ? (viewportSizes.map(([w, h]) => ({
                    w,
                    h,
                })) as MagniteSize[])
                : viewportSizes;
            return {
                ...slotMap,
                sizes: filteredSizes,
            } as T;
        }
    }
    return {
        ...slotMap,
        sizes: sizes,
    } as T;
};
export const removeSizeMapping = <T extends AmazonSlotMapEntry | MagniteSlotMapEntry,>(slotMap: T): Omit<T, "sizeMapping"> => {
    const { sizeMapping: _sizeMapping, ...slotMapCore } = slotMap;
    return slotMapCore;
};
export const refreshSlot = (slot: googletag.Slot) => {
    const gt = getGoogleTag();
    gt.cmd.push(() => {
        slot.setConfig({ targeting: { refresh: "true" } });
        gt.pubads().refresh([slot]);
    });
};
export const getSlotsFromGoogleTag = (): Promise<googletag.Slot[]> => {
    const gt = getGoogleTag();
    return new Promise((resolve) => {
        gt.cmd.push(() => {
            const slots = googletag.pubads().getSlots();
            resolve(slots);
        });
    });
};
export const getSlotById = (slotId: string): Promise<googletag.Slot | undefined> => {
    const gt = getGoogleTag();
    return new Promise((resolve) => {
        gt.cmd.push(() => {
            const slot = googletag
                .pubads()
                .getSlots()
                .find((s) => s.getSlotElementId() === slotId);
            resolve(slot);
        });
    });
};
export const normalizeToSingleSizeArray = (sizes: googletag.GeneralSize[]): googletag.SingleSizeArray[] => {
    return sizes.flatMap((size) => {
        if (isSingleSizeArray(size)) {
            return [size];
        }
        if (isSizeObject(size)) {
            return [[size.width, size.height]];
        }
        if (isMultiSizeArray(size)) {
            return size;
        }
        return [];
    });
};
export const isSlotSizeLargerThan = (minSize: number) => {
    return (size: googletag.GeneralSize): boolean => {
        if (isSingleSizeArray(size)) {
            const [width, height] = size;
            return width >= minSize && height >= minSize;
        }
        return false;
    };
};
export const hasEligibleSizeForRefresh = (slot: googletag.Slot): boolean => {
    let slotSizes: googletag.GeneralSize[] = [];
    try {
        // @ts-expect-error: getSizes is not officially part of Slot type
        const rawSizes = slot.getSizes();
        slotSizes = normalizeToSingleSizeArray(rawSizes);
    }
    catch (error) {
        console.warn("getSizes() failed or is unsupported:", error);
        return false;
    }
    if (slotSizes.length === 0) {
        return false;
    }
    const isEligibleSize = isSlotSizeLargerThan(REFRESH_MIN_WIDTH);
    return slotSizes.some(isEligibleSize);
};
export const checkRecommenderSlot = (slotId: string): boolean => slotId.includes("mantis-carousel");
export const getSlotElementById = (slotId: string) => {
    return document.querySelector(`#${slotId}`);
};
export const getElementByIdPrefix = (prefix: string) => {
    return document.querySelector(`[id^="${prefix}"]`);
};
/** Sanitizes external targeting values, keeping only valid SlotTargetingEntry and dropping non-standard entries */
export const sanitizeSlotTargeting = (targeting: ExternalSlotTargeting): SlotTargeting => Object.fromEntries(Object.entries(targeting).filter(isSlotTargetingEntry));
/** Normalizes incoming external slots by sanitizing targeting to internal SlotTargeting type */
export const normalizeSlots = (slots: (DisplaySlotConfig | VideoSlotConfig | {
    targeting: ExternalSlotTargeting;
})[]): (DisplaySlotConfig | VideoSlotConfig)[] => slots.map((slot) => {
    if ("targeting" in slot) {
        // as DisplaySlotConfig — targeting has been sanitized from external format to SlotTargeting
        return {
            ...slot,
            targeting: sanitizeSlotTargeting(slot.targeting),
        } as DisplaySlotConfig;
    }
    return slot as VideoSlotConfig;
});
